// src/components/settings/TeamsPanel.js
//
// Splits one company into teams. Each team has exactly one Main Admin, plus
// dispatchers and drivers.
//
// Drag-and-drop uses the browser's native HTML5 API rather than a library —
// this is a desktop admin screen, and it avoids adding a dependency that has to
// keep pace with React. Every drag target is also clickable, so it works on a
// tablet and with a keyboard.
//
// Unassigned people stay in the pool and remain visible to the whole company:
// creating a team never hides anyone.
//
// Each driver sits on one row with a line to the dispatcher who runs them —
// solid in the team's colour, red and dashed when nobody does. A Super Admin
// can rename a team (✎). Who may set a driver's dispatcher is decided per team
// by canLinkInTeam: a Main Admin can on their own team and nowhere else.

import React, { useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAppearance } from '../../contexts/AppearanceContext';
import { DEFAULT_CURRENT_STATUSES } from '../loads/utils/constants';
import {
  MAX_TEAMS,
  getCompanyTeams,
  getTeamColor,
  getUserTeamIds,
  makeTeamId,
  nextTeamColor,
  findTeamMainAdmin,
} from '../../utils/teams';

const roleList = (user) =>
  Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean);

const isMainAdmin = (user) => roleList(user).includes('Main Admin');
const isDispatcher = (user) => roleList(user).includes('Dispatcher');

// The one role worth showing on a chip, most significant first.
const ROLE_PRIORITY = ['Super Admin', 'Main Admin', 'Admin', 'Dispatcher', 'Accountant', 'HR', 'Fleet'];
const primaryRoleLabel = (user) => {
  const roles = roleList(user);
  return ROLE_PRIORITY.find(r => roles.includes(r)) || roles[0] || 'User';
};

// Inactive people are hidden: a disabled user or a driver who is not Active has
// no business being assigned to a team.
const isActiveUser = (u) => u?.active !== false;
const isActiveDriver = (d) => !d?.isDeleted && d?.status !== 'Deleted' && d?.status === 'Active';

export default function TeamsPanel({
  company,
  users,
  drivers,
  canManage,
  onSaveTeams,        // (teams) => Promise
  onAssignPerson,     // ({ kind: 'user'|'driver', id, teamId, loadAccess, moveLoads }) => Promise
  onLinkDispatcher,   // (driverId, dispatcherId|null) => Promise
  onManageDrivers,    // opens the picker for adding drivers TO the company
  onManageUsers,      // ...and for users
  canLinkInTeam = () => canManage, // (teamId) => may this viewer set dispatchers here
  busy,
}) {
  const teams = getCompanyTeams(company);
  const { isNight } = useAppearance();
  const [dragging, setDragging] = useState(null);   // { kind, id, name }
  const [selected, setSelected] = useState(null);   // click-to-assign fallback
  const [dropTarget, setDropTarget] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  // Which team's "add somebody" picker is open, if any.
  //
  // Drag-and-drop was the only way to put a person on a team, and the only
  // instruction for it was small grey text inside a different box further down
  // the page. People looked at an empty team, found nothing to click, and
  // concluded it could not be done. Dragging still works; it is just no longer
  // the only thing that does.
  // The "Add to team" list is open on every team from the start; it used to
  // appear only after clicking "+ Add", and nobody found it. Each team can be
  // folded, and folding is remembered only until the page is left.
  const [foldedAdd, setFoldedAdd] = useState(() => new Set());
  const toggleAdd = (teamId) => setFoldedAdd(prev => {
    const next = new Set(prev);
    if (next.has(teamId)) next.delete(teamId); else next.add(teamId);
    return next;
  });
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, name }
  // The question asked when a dispatcher leaves a team: what may they still do
  // with their own unfinished loads there? { name, resolve } while it is open.
  const [accessAsk, setAccessAsk] = useState(null);
  const askLoadAccess = (name, asMainAdmin) => new Promise(resolve => setAccessAsk({ name, asMainAdmin, resolve }));
  const answerLoadAccess = (value) => {
    if (accessAsk) accessAsk.resolve(value);
    setAccessAsk(null);
  };
  // The window shown before a driver is moved in from another company. It
  // resolves { profileCompany } on Move (true when the box was ticked), null on
  // Cancel.
  const [driverMoveAsk, setDriverMoveAsk] = useState(null);
  const [driverMoveProfile, setDriverMoveProfile] = useState(false);
  const askDriverMove = (info) => new Promise(resolve => {
    setDriverMoveProfile(false);
    setDriverMoveAsk({ ...info, resolve });
  });
  const answerDriverMove = (value) => {
    if (driverMoveAsk) driverMoveAsk.resolve(value);
    setDriverMoveAsk(null);
  };

  const saveRename = async () => {
    const name = (renaming?.name || '').trim().slice(0, 40);
    const team = teams.find(t => t.id === renaming?.id);
    setRenaming(null);
    if (!team || !name || name === team.name) return;
    // Only the label changes. Loads, drivers and users point at the team id,
    // which stays the same, so nothing else needs touching.
    await onSaveTeams(teams.map(t => (t.id === team.id ? { ...t, name } : t)));
  };

  // A lone team gets the whole width, with its drivers spread across columns,
  // rather than one narrow column and a long list down the page.
  const wide = teams.length === 1;
  const teamGridClass = teams.length === 1
    ? 'grid grid-cols-1 gap-3 mb-4'
    : teams.length === 2
      ? 'grid grid-cols-1 md:grid-cols-2 gap-3 mb-4'
      : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-4';
  const driverListClass = teams.length === 1
    ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-1.5'
    : teams.length === 2
      ? 'grid grid-cols-1 xl:grid-cols-2 gap-x-3 gap-y-1'
      : 'space-y-1';
  // One Driver / Dispatcher heading per column the driver list shows at each screen width.
  const headingSlots = teams.length === 1
    ? ['flex', 'hidden md:flex', 'hidden xl:flex', 'hidden 2xl:flex']
    : teams.length === 2 ? ['flex', 'hidden xl:flex'] : ['flex'];

  // People who belong to this company
  const companyUsers = users.filter(u =>
    (u.assignedParentCompanyIds || []).includes(company.id) && isActiveUser(u)
  );
  const companyDrivers = drivers.filter(d => d.parentCompanyId === company.id && isActiveDriver(d));

  const membersOf = (teamId) => ({
    admin: companyUsers.find(u => isMainAdmin(u) && getUserTeamIds(u).includes(teamId)) || null,
    dispatchers: companyUsers.filter(u => isDispatcher(u) && !isMainAdmin(u) && getUserTeamIds(u).includes(teamId)),
    drivers: companyDrivers.filter(d => d.teamId === teamId),
    // Everyone else on the team — Admin, Accountant, HR, Fleet. Without a
    // place of their own they left the "not in a team" pool and appeared
    // nowhere, so they could not be seen or taken off again.
    others: companyUsers.filter(u => !isMainAdmin(u) && !isDispatcher(u) && getUserTeamIds(u).includes(teamId)),
  });

  // "Available here" means not in a team OF THIS COMPANY — not "in no team at
  // all". Someone already on a team in another company must still appear, or a
  // Super Admin simply cannot move or share them: they vanish from every pool
  // once they join their first team. A user can hold teams in several companies
  // at once, and the save path already preserves the ones elsewhere.
  const companyTeamIds = new Set(teams.map(t => t.id));
  const isInATeamHere = (person, ids) => ids.some(id => companyTeamIds.has(id));

  const unassignedUsers = companyUsers.filter(u => !isInATeamHere(u, getUserTeamIds(u)));
  // Not on one of THIS company's teams — which includes a driver still pointing
  // at a team that was deleted, or at a team of a company they used to belong
  // to. Counting only an empty teamId hid those drivers everywhere: no team box
  // listed them and neither did this pool.
  const unassignedDrivers = companyDrivers.filter(d => !companyTeamIds.has(d.teamId));
  const hasStaleTeam = (d) => !!d.teamId && !companyTeamIds.has(d.teamId);

  // Drivers a Super Admin may want but who are not in this company.
  //
  // Two different situations, kept apart because the consequences differ:
  //
  //   unclaimed — nobody owns them. Adding one takes nothing from anybody, and
  //               these are the drivers who show up in no company's box at all,
  //               which is the thing that makes a Super Admin think the list is
  //               broken.
  //   elsewhere — owned by another company. Adding one MOVES them, and the
  //               other company loses them. Shown, because otherwise a Super
  //               Admin cannot move anyone without leaving this screen, but
  //               shown separately and with the owner named.
  const unclaimedDrivers = drivers.filter(d =>
    isActiveDriver(d) && !d.parentCompanyId);
  const driversElsewhere = drivers.filter(d =>
    isActiveDriver(d) && d.parentCompanyId && d.parentCompanyId !== company.id);

  const [showOutside, setShowOutside] = useState(false);
  const [outsideSearch, setOutsideSearch] = useState('');

  const matchesSearch = (d) => {
    const q = outsideSearch.trim().toLowerCase();
    if (!q) return true;
    return `${d.name || ''} ${d.parentCompanyName || ''}`.toLowerCase().includes(q);
  };

  const visibleUnclaimed = unclaimedDrivers.filter(matchesSearch);
  const visibleElsewhere = driversElsewhere.filter(matchesSearch);
  const outsideTotal = unclaimedDrivers.length + driversElsewhere.length;

  const handleCreateTeam = async () => {
    const name = newTeamName.trim();
    if (!name || teams.length >= MAX_TEAMS) return;
    await onSaveTeams([
      ...teams,
      { id: makeTeamId(company.id), name, color: nextTeamColor(teams) },
    ]);
    setNewTeamName('');
    setAdding(false);
  };

  const handleDeleteTeam = async (team) => {
    const m = membersOf(team.id);
    const count = (m.admin ? 1 : 0) + m.dispatchers.length + m.others.length + m.drivers.length;
    const warning = count > 0
      ? `\n\n${count} ${count === 1 ? 'person goes' : 'people go'} back to the unassigned pool. Nobody loses access — they stay visible to the whole company.`
      : '';
    if (!window.confirm(`Delete team "${team.name}"?${warning}`)) return;
    await onSaveTeams(teams.filter(t => t.id !== team.id));
  };

  const assign = async (person, teamId) => {
    if (!person) return;

    // Exactly one Main Admin per team.
    if (teamId && person.kind === 'user' && person.isMainAdmin) {
      const existing = findTeamMainAdmin(companyUsers, teamId);
      if (existing && existing.id !== person.id) {
        window.alert(
          `${teams.find(t => t.id === teamId)?.name || 'This team'} already has a Main Admin (${existing.name || existing.email}).\n\n` +
          `Move them out first — a team can only have one.`
        );
        return;
      }
    }

    // A user from another company: adding them here MOVES them (see
    // useTeamActions). Said in full before anything is written.
    if (teamId && person.kind === 'user' && person.fromOutside) {
      const target = teams.find(t => t.id === teamId)?.name || 'this team';
      const from = person.fromCompanyNames || 'another company';
      const ok = window.confirm(
        `Move ${person.name} to ${target} in ${company.name}?\n\n` +
        `They are now in ${from}.\n\n` +
        `• They leave every team and company they are in now (${from})\n` +
        `• They join ${target} and ${company.name}\n` +
        `• Loads they were dispatching: they can still see them until delivered, but not change them`
      );
      if (!ok) return;
    }

    // Taking a driver off another company is the one assignment here with a
    // consequence somewhere else, so it asks first and names what changes.
    // Everything else on this screen is reversible housekeeping within one
    // company; this is not, from the other company's point of view.
    // The driver's profile company is what their BOL paperwork prints. It is
    // not changed by the move unless the Super Admin ticks the box.
    let profileCompany = false;
    if (teamId && person.kind === 'driver' &&
        person.fromCompanyId && person.fromCompanyId !== company.id) {
      const driver = drivers.find(d => d.id === person.id);
      const answer = await askDriverMove({
        name: person.name,
        fromCompany: person.fromCompanyName || 'another company',
        target: teams.find(t => t.id === teamId)?.name || 'this team',
        bolCompany: driver?.assignedCompanyName || null,
      });
      if (!answer) return;
      profileCompany = answer.profileCompany;
    }

    // A driver moving from one of this company's teams to another takes their
    // unfinished loads along (useTeamActions). The old team still sees those
    // loads, marked as managed by the new team, but can no longer change them.
    let moveLoads = false;
    if (teamId && person.kind === 'driver' && person.fromCompanyId === company.id &&
        person.fromTeamId && person.fromTeamId !== teamId &&
        teams.some(t => t.id === person.fromTeamId)) {
      const target = teams.find(t => t.id === teamId)?.name || 'the new team';
      if (!window.confirm(`This will move ${person.name} including all current loads to team ${target}.`)) return;
      moveLoads = true;
    }

    // Taking a driver off a team leaves their current loads with that team.
    // Removing and then adding to another team looks like a move but is not
    // one, so the loads were left behind without a word. Say so, and point to
    // the move that takes them along. If the count cannot be read, removing
    // goes ahead as before.
    if (!teamId && person.kind === 'driver' && person.fromTeamId &&
        teams.some(t => t.id === person.fromTeamId)) {
      const driver = drivers.find(d => d.id === person.id);
      let count = 0;
      try {
        if (driver?.tenantId) {
          const snap = await getDocs(query(
            collection(db, 'loads'),
            where('tenantId', '==', driver.tenantId),
            where('driverId', '==', person.id),
            where('status', 'in', DEFAULT_CURRENT_STATUSES),
          ));
          count = snap.docs.filter(d => !d.data().isArchived &&
            (!d.data().teamId || d.data().teamId === person.fromTeamId)).length;
        }
      } catch (error) {
        console.error('Teams: could not count current loads', error);
      }
      if (count > 0) {
        const from = teams.find(t => t.id === person.fromTeamId)?.name || 'this team';
        const ok = window.confirm(
          `${person.name} has ${count} current ${count === 1 ? 'load' : 'loads'} in team ${from}. ` +
          `${count === 1 ? 'It' : 'They'} will stay with team ${from}.\n\n` +
          `To move ${person.name} together with ${count === 1 ? 'this load' : 'these loads'}, ` +
          `press Cancel and drag ${person.name} straight onto the other team.\n\n` +
          `Press OK to remove anyway.`
        );
        if (!ok) return;
      }
    }

    // A dispatcher or Main Admin leaving one of this company's teams: the Super
    // Admin decides what happens to the loads they were running there
    // (utils/loadAccess).
    // Closing the question cancels the move.
    let loadAccess;
    if (person.kind === 'user') {
      const user = companyUsers.find(u => u.id === person.id);
      const leaving = getUserTeamIds(user).some(t => t !== teamId && teams.some(x => x.id === t));
      if (leaving && (isDispatcher(user) || isMainAdmin(user))) {
        loadAccess = await askLoadAccess(person.name, isMainAdmin(user));
        if (!loadAccess) return;
      }
    }

    await onAssignPerson({ kind: person.kind, id: person.id, teamId, loadAccess, moveLoads, profileCompany });
    setSelected(null);
    setDragging(null);
  };

  const onDrop = (teamId) => (e) => {
    e.preventDefault();
    setDropTarget(null);
    if (dragging) assign(dragging, teamId);
  };

  // Native HTML5 drag needs data actually set on the event. Without a
  // setData() call some browsers never start the drag at all — which is why
  // dragging did nothing while click-to-assign worked.
  const dragProps = (person) => canManage ? {
    draggable: true,
    onDragStart: (e) => {
      setDragging(person);
      try {
        e.dataTransfer.setData('text/plain', `${person.kind}:${person.id}`);
        e.dataTransfer.effectAllowed = 'move';
      } catch (_) {
        // Some browsers restrict dataTransfer; the React state still carries it.
      }
    },
    onDragEnd: () => { setDragging(null); setDropTarget(null); },
  } : {};

  // hideRole: inside a team box every section is already headed (Main Admin,
  // Dispatchers, Driver), so the label on each chip only repeats it.
  const Chip = ({ person, onRemove, hideRole }) => {
    const active = selected?.kind === person.kind && selected?.id === person.id;
    return (
      <span
        {...dragProps(person)}
        onClick={() => canManage && setSelected(active ? null : person)}
        className={
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs border select-none max-w-full ' +
          (canManage ? 'cursor-pointer ' : '') +
          (active ? 'ring-2 ring-blue-400 ' : '') +
          (person.kind === 'driver'
            ? 'bg-white border-gray-300 text-gray-700'
            : person.isMainAdmin
              ? 'bg-indigo-50 border-indigo-200 text-indigo-800 font-medium'
              : 'bg-gray-50 border-gray-300 text-gray-700')
        }
        title={canManage ? 'Drag to a team, or tap then tap a team' : undefined}
      >
        {!hideRole && <span className="text-[10px] uppercase tracking-wide opacity-70 flex-shrink-0">{person.roleLabel}</span>}
        <span className="truncate">{person.name}</span>
        {onRemove && canManage && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-gray-400 hover:text-red-600 leading-none"
            title="Remove from team"
          >
            ×
          </button>
        )}
      </span>
    );
  };

  const toPerson = (u) => ({
    kind: 'user',
    id: u.id,
    name: u.name || u.email,
    isMainAdmin: isMainAdmin(u),
    roleLabel: primaryRoleLabel(u),
  });
  const outsideToPerson = (u) => ({
    ...toPerson(u),
    fromOutside: true,
    fromCompanyNames: (u.assignedParentCompanyNames || []).filter(Boolean).join(', ') || null,
  });

  // Staff of other companies, who can be brought onto a team here — which
  // moves them (useTeamActions). Super Admins are on no team.
  const outsideUsers = users.filter(u =>
    isActiveUser(u) &&
    !(u.assignedParentCompanyIds || []).includes(company.id) &&
    !roleList(u).includes('Super Admin') &&
    ['Main Admin', 'Admin', 'Dispatcher'].some(r => roleList(u).includes(r))
  );
  const driverToPerson = (d) => ({
    kind: 'driver', id: d.id, name: d.name || d.id, roleLabel: 'Driver',
    // Carried so assign() can warn before taking someone off another company.
    fromCompanyId: d.parentCompanyId || null,
    fromCompanyName: d.parentCompanyName || null,
    fromTeamId: d.teamId || null,
  });

  return (
    <div className="border-t border-gray-100 px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">
            Teams ({teams.length}/{MAX_TEAMS})
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            One Main Admin per team. Anyone not in a team stays visible to the whole company.
          </p>
        </div>
        {canManage && teams.length < MAX_TEAMS && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            + Add team
          </button>
        )}
      </div>

      {adding && (
        <div className="flex gap-2 mb-3">
          <input
            autoFocus
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTeam(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="Team name, e.g. Night dispatch"
            className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          />
          <button
            onClick={handleCreateTeam}
            disabled={!newTeamName.trim() || busy}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded-md text-sm font-medium"
          >
            Create
          </button>
          <button
            onClick={() => { setAdding(false); setNewTeamName(''); }}
            className="text-gray-600 px-2 text-sm"
          >
            Cancel
          </button>
        </div>
      )}

      {selected && (
        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-3">
          <strong>{selected.name}</strong> selected — tap a team to add them, or tap the pool to unassign.
        </p>
      )}

      {/* Team boxes */}
      {teams.length > 0 && (
        <div className={teamGridClass}>
          {teams.map((team, i) => {
            const c = getTeamColor(team.color, i, isNight);
            const m = membersOf(team.id);
            const isTarget = dropTarget === team.id;

            return (
              <div
                key={team.id}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropTarget(team.id); }}
                onDragLeave={() => setDropTarget(null)}
                onDrop={onDrop(team.id)}
                onClick={() => selected && assign(selected, team.id)}
                style={{ backgroundColor: c.bg, borderColor: isTarget ? c.dot : c.border }}
                className={'rounded-lg border-2 transition-shadow ' + (wide ? 'p-4 ' : 'p-3 min-h-[170px] ') + (isTarget ? 'shadow-md' : '')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.dot }} />
                    {renaming?.id === team.id ? (
                      <input
                        autoFocus
                        value={renaming.name}
                        maxLength={40}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenaming({ id: team.id, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                        onBlur={saveRename}
                        className="text-sm font-semibold border border-gray-300 rounded px-1.5 py-0.5 min-w-0 w-48"
                        style={{ color: c.text }}
                      />
                    ) : (
                      <>
                        <span className={(wide ? 'text-base ' : 'text-sm ') + 'font-semibold truncate'} style={{ color: c.text }}>{team.name}</span>
                        {canManage && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setRenaming({ id: team.id, name: team.name }); }}
                            disabled={busy}
                            className="text-gray-400 hover:text-gray-700 text-xs flex-shrink-0"
                            title="Rename team"
                            aria-label={`Rename ${team.name}`}
                          >
                            ✎
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleAdd(team.id); }}
                        className="text-[11px] font-semibold px-2 py-0.5 rounded border bg-white hover:bg-gray-50"
                        style={{ color: c.text, borderColor: c.border }}
                        title={foldedAdd.has(team.id) ? 'Show people you can add to this team' : 'Hide the list of people to add'}
                      >
                        {foldedAdd.has(team.id) ? '▸ Show people to add' : '▾ Hide people to add'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team); }}
                        className="text-xs text-gray-400 hover:text-red-600"
                        title="Delete team"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className={wide ? 'flex flex-wrap gap-x-10 gap-y-2' : 'space-y-2'}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Main Admin</p>
                    {m.admin ? (
                      <Chip
                        person={toPerson(m.admin)}
                        hideRole
                        onRemove={() => assign(toPerson(m.admin), null)}
                      />
                    ) : (
                      <p className="text-xs text-gray-400 italic">None yet</p>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                      Dispatchers ({m.dispatchers.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {m.dispatchers.length === 0
                        ? <span className="text-xs text-gray-400 italic">None</span>
                        : m.dispatchers.map(u => (
                            <Chip key={u.id} person={toPerson(u)} hideRole onRemove={() => assign(toPerson(u), null)} />
                          ))}
                    </div>
                  </div>

                  {m.others.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        Also on this team ({m.others.length})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {/* Role shown: this row mixes Admin, Accountant, HR, Fleet. */}
                        {m.others.map(u => (
                          <Chip key={u.id} person={toPerson(u)} onRemove={() => assign(toPerson(u), null)} />
                        ))}
                      </div>
                    </div>
                  )}
                  </div>

                  <div>
                    {(() => {
                      // Drivers on this team that nobody runs yet. Counted here
                      // so the gap is visible on the team itself, not only on
                      // the row somebody has to scroll to.
                      const unlinked = m.dispatchers.length > 0
                        ? m.drivers.filter(d => !m.dispatchers.some(u => u.id === d.dispatcherId))
                        : [];

                      return (
                        <p className="text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1">
                          <span className="text-gray-500">Drivers ({m.drivers.length})</span>
                          {unlinked.length > 0 && (
                            <span className="text-red-600 font-bold normal-case tracking-normal">
                              — {unlinked.length} need{unlinked.length === 1 ? 's' : ''} a dispatcher
                            </span>
                          )}
                          {m.drivers.length > 0 && m.dispatchers.length === 0 && (
                            <span className="text-amber-600 font-semibold normal-case tracking-normal">
                              — add a dispatcher to this team
                            </span>
                          )}
                        </p>
                      );
                    })()}
                    {m.drivers.length > 0 && m.dispatchers.length > 0 && (
                      <div className={driverListClass + ' mb-0.5'} aria-hidden="true">
                        {headingSlots.slice(0, m.drivers.length).map((vis, k) => (
                          <div key={k} className={'gap-2 px-1.5 text-[10px] uppercase tracking-wide text-gray-400 ' + vis}>
                            <span className="flex-1">Driver</span><span className="w-36">Dispatcher</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={driverListClass}>
                      {m.drivers.length === 0
                        ? <span className="text-xs text-gray-400 italic">None</span>
                        : m.drivers.map(d => {
                            // A link only counts while that dispatcher is still on
                            // this team — otherwise a dispatcher who moved away
                            // would appear to be running drivers they cannot see.
                            const linked = m.dispatchers.find(u => u.id === d.dispatcherId) || null;

                            return (
                              <div key={d.id} className={'flex items-center min-w-0 rounded-md px-1.5 py-1 ' + (isNight ? 'bg-gray-900/40' : 'bg-white/70')}>
                                <div className="min-w-0 max-w-[60%] flex-shrink" title={d.name || ''}>
                                  <Chip person={driverToPerson(d)} hideRole onRemove={() => assign(driverToPerson(d), null)} />
                                </div>

                                {/* The line from driver to dispatcher: solid in the team's
                                    colour when someone runs this driver, red and dashed when
                                    nobody does — the gap is visible without reading. */}
                                {m.dispatchers.length > 0 ? (
                                  <div className="flex-1 min-w-[36px] flex items-center px-1" aria-hidden="true">
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: linked ? c.dot : '#f87171' }} />
                                    <span className="flex-1"
                                      style={{ borderTop: `2px ${linked ? 'solid' : 'dashed'} ${linked ? c.dot : '#f87171'}`, opacity: linked ? 0.7 : 1 }} />
                                    <span className="flex-shrink-0 w-0 h-0"
                                      style={{ borderTop: '4px solid transparent', borderBottom: '4px solid transparent',
                                        borderLeft: `6px solid ${linked ? c.dot : '#f87171'}`, opacity: linked ? 0.7 : 1 }} />
                                  </div>
                                ) : <div className="flex-1" />}

                                {m.dispatchers.length > 0 && (
                                <div className="w-36 flex-shrink-0">
                                {!canLinkInTeam(team.id) && (
                                  <span className={'block truncate text-[11px] ' + (linked ? 'text-gray-600' : 'text-red-600 font-semibold')}
                                    title={linked ? (linked.name || linked.email) : undefined}>
                                    {linked ? `→ ${linked.name || linked.email}` : '⚠ No dispatcher'}
                                  </span>
                                )}

                                {m.dispatchers.length > 0 && canLinkInTeam(team.id) && (
                                  <select
                                    value={linked ? linked.id : ''}
                                    disabled={busy}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      onLinkDispatcher(d.id, e.target.value || null);
                                    }}
                                    className={
                                      'w-full text-[11px] border rounded px-1 py-0.5 ' +
                                      (linked
                                        ? 'text-gray-700 border-gray-300 bg-white'
                                        : 'text-red-700 border-red-400 bg-red-50 font-semibold')
                                    }
                                    title={linked
                                      ? 'Which dispatcher runs this driver'
                                      : 'Nobody runs this driver yet — they are invisible to every dispatcher until one is chosen'}
                                  >
                                    <option value="">{linked ? 'No dispatcher' : '⚠ Choose dispatcher'}</option>
                                    {m.dispatchers.map(u => (
                                      <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                    ))}
                                  </select>
                                )}
                                </div>
                                )}
                              </div>
                            );
                          })}
                    </div>
                  </div>
                </div>

                {/* The people who can join this team, open by default. Lists exactly the
                    people who can join this team, so there is no hunting and
                    no dragging — clicking a name puts them on the team. */}
                {canManage && !foldedAdd.has(team.id) && (() => {
                  const alreadyHere = new Set([
                    ...(m.admin ? [m.admin.id] : []),
                    ...m.dispatchers.map(u => u.id),
                    ...m.others.map(u => u.id),
                    ...m.drivers.map(d => d.id),
                  ]);
                  const choices = [
                    ...companyUsers.filter(u => !alreadyHere.has(u.id)).map(toPerson),
                    ...companyDrivers.filter(d => !alreadyHere.has(d.id)).map(driverToPerson),
                  ];
                  const outsideChoices = outsideUsers.map(outsideToPerson);

                  return (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 pt-2 border-t border-dashed"
                      style={{ borderColor: c.border }}
                    >
                      {choices.length === 0 && outsideChoices.length === 0 ? (
                        <p className="text-[11px] text-gray-500 italic">
                          Everybody in {company.name} is already on this team. Use
                          “+ Drivers” below to bring somebody into the company first.
                        </p>
                      ) : (
                        <>
                          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                            Click a name to add to {team.name}
                          </p>
                          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                            {choices.map(person => (
                              <button
                                key={`${person.kind}-${person.id}`}
                                onClick={() => assign(person, team.id)}
                                disabled={busy}
                                className="text-[11px] bg-white border border-gray-300 rounded-full px-2 py-0.5
                                           hover:border-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              >
                                {person.kind === 'driver' ? '🚚' : '👤'} {person.name}
                              </button>
                            ))}
                          </div>

                          {outsideChoices.length > 0 && (
                            <>
                              <p className="text-[10px] uppercase tracking-wide text-amber-700 mt-2 mb-1">
                                From other companies — adding moves them here
                              </p>
                              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                                {outsideChoices.map(person => (
                                  <button
                                    key={`out-${person.id}`}
                                    onClick={() => assign(person, team.id)}
                                    disabled={busy}
                                    title={person.fromCompanyNames ? `Now in ${person.fromCompanyNames}` : undefined}
                                    className="text-[11px] bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5
                                               hover:border-amber-500 disabled:opacity-50"
                                  >
                                    👤 {person.name}
                                    {person.fromCompanyNames && (
                                      <span className="text-amber-700"> · {person.fromCompanyNames}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Unassigned pool */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropTarget('pool'); }}
        onDragLeave={() => setDropTarget(null)}
        onDrop={onDrop(null)}
        onClick={() => selected && assign(selected, null)}
        className={
          'rounded-lg border-2 border-dashed p-4 min-h-[120px] ' +
          (dropTarget === 'pool' ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50')
        }
      >
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              Not in a team here ({unassignedUsers.length + unassignedDrivers.length})
            </p>
            <p className="text-xs text-gray-500">
              Visible to everyone in {company.name}.{' '}
              {canManage && 'Click a name under a team above, or drag someone into it.'}
            </p>
          </div>

          {/* Adding people to the company happens here rather than in separate
              boxes — one place to manage who is in this company and who is on
              which team. */}
          {/* Only where the caller wires them up (Company Management). On the
              Teams tab they had nothing to open and only showed a message. */}
          {canManage && (onManageDrivers || onManageUsers) && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onManageDrivers && onManageDrivers(); }}
                className="text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded px-2.5 py-1.5 hover:bg-blue-50"
                title={`Bring a driver into ${company.name}. To put one on a team, click their name under the team.`}
              >
                Drivers in company
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onManageUsers && onManageUsers(); }}
                className="text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded px-2.5 py-1.5 hover:bg-blue-50"
                title={`Bring a user into ${company.name}. To put one on a team, click their name under the team.`}
              >
                Users in company
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {unassignedUsers.length === 0 && unassignedDrivers.length === 0 ? (
            <span className="text-xs text-gray-400 italic">Everyone in this company is on a team</span>
          ) : (
            <>
              {unassignedUsers.map(u => <Chip key={u.id} person={toPerson(u)} />)}
              {unassignedDrivers.map(d => (
                <span key={d.id} className="inline-flex flex-col">
                  <Chip person={driverToPerson(d)} />
                  {hasStaleTeam(d) && (
                    <span className="text-[10px] text-amber-700 pl-2 pt-0.5">
                      {String(d.teamId).startsWith(`${company.id}_team_`)
                        ? 'was on a team that no longer exists'
                        : 'still on a team of another company'}
                    </span>
                  )}
                </span>
              ))}
            </>
          )}
        </div>

        {/* ----------------------------------------------------------------
            Drivers from outside this company.
            Collapsed by default: this is the long list, and most of the time
            a Super Admin is working within one company and does not want it.
            ---------------------------------------------------------------- */}
        {canManage && outsideTotal > 0 && (
          <div className="mt-4 pt-3 border-t-2 border-gray-300">
            <button
              onClick={(e) => { e.stopPropagation(); setShowOutside(v => !v); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900"
            >
              <span className={`inline-block transition-transform ${showOutside ? 'rotate-90' : ''}`}>▶</span>
              Drivers from elsewhere ({outsideTotal})
            </button>

            {!showOutside && (
              <p className="text-xs text-gray-500 mt-1">
                Drivers with no company, and drivers who belong to a different company.
              </p>
            )}

            {showOutside && (
              <div className="mt-3">
                {outsideTotal > 12 && (
                  <input
                    type="text"
                    value={outsideSearch}
                    onChange={(e) => setOutsideSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search by driver or company name…"
                    className="w-full sm:w-72 mb-3 text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {/* Safe to take: nobody owns these. */}
                {visibleUnclaimed.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-1.5">
                      No company yet ({visibleUnclaimed.length})
                      <span className="font-normal text-gray-400"> — free to add, nothing is taken from anyone</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleUnclaimed.map(d => (
                        <Chip key={d.id} person={driverToPerson(d)} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Owned elsewhere: adding one of these MOVES them. */}
                {visibleElsewhere.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-800 mb-1.5">
                      Belong to another company ({visibleElsewhere.length})
                      <span className="font-normal text-amber-600"> — adding one moves them, and that company loses them</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleElsewhere.map(d => (
                        <span key={d.id} className="inline-flex flex-col">
                          <Chip person={driverToPerson(d)} />
                          <span className="text-[10px] text-amber-700 pl-2 pt-0.5">
                            {d.parentCompanyName || 'another company'}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {visibleUnclaimed.length === 0 && visibleElsewhere.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No drivers match “{outsideSearch}”.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {driverMoveAsk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => answerDriverMove(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Move {driverMoveAsk.name} to {company.name}?</h3>
            <p className="mt-1 text-xs text-gray-500">They now belong to {driverMoveAsk.fromCompany}.</p>
            <ul className="mt-3 text-sm text-gray-700 list-disc pl-5 space-y-1">
              <li>They join {driverMoveAsk.target} in {company.name}.</li>
              <li>Their current loads stay with {driverMoveAsk.fromCompany}, unless their dispatcher is with them again in the new team.</li>
              <li>Their dispatcher from the old team is removed. Choose one here.</li>
            </ul>
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                <i className="fas fa-triangle-exclamation mr-1" aria-hidden="true" />
                Their BOL company{driverMoveAsk.bolCompany ? <> is still <b>{driverMoveAsk.bolCompany}</b></> : ' is not changed'}.
                {' '}If you want to change the BOL company for this driver, please update the driver profile company too.
              </p>
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={driverMoveProfile}
                  onChange={e => setDriverMoveProfile(e.target.checked)}
                />
                Update driver profile company too ({company.name})
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => answerDriverMove(null)} className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => answerDriverMove({ profileCompany: driverMoveProfile })} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {accessAsk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => answerLoadAccess(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">{accessAsk.name}'s unfinished loads?</h3>
            <p className="mt-1 text-xs text-gray-500">
              {accessAsk.asMainAdmin
                ? "All of the team's unfinished loads, until delivered."
                : 'Only loads they dispatch or booked themselves, until delivered.'}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button onClick={() => answerLoadAccess('manage')} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
                See and manage
              </button>
              <button onClick={() => answerLoadAccess('view')} className="px-3 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm font-medium hover:bg-gray-50">
                See only
              </button>
              <button onClick={() => answerLoadAccess('none')} className="px-3 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50">
                No access
              </button>
              <button onClick={() => answerLoadAccess(null)} className="mt-1 text-xs text-gray-500 hover:underline">
                Cancel — keep them on the team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
